import {Link as RouterLink} from "react-router-dom";
import React from "react";

export const standardNewBizStats = ({mqlLabel, salLabel, currUser, acceptedBookingMetricString}) => {

    return currUser && acceptedBookingMetricString && [
        // ... existing charts
        {
            id: 'transactions',
            "voiceover": "This chart shows the # of new business transactions.",
            link: "#transactions",
            title: "Transactions",
            props: {
                chartTitle: 'transactions',
                chartColorId: 'transactions',
                usedInPredictions: false,
                availableGlobalOnly: false,
                drilldownAvailable: true,
                chartDescription: `This chart shows the # of new business transactions {segmentDescription}`,
                inputType: 'integer',
                bottomLegendLabel: 'Close Date',
                legendLabel: '# deals',
                handleChartClick: 'handleTransactionsChartClick',
                trendLineEntry: '# Deals',
                qPilotEnabled: true,
                qPilotChartName: 'gtm-stats-transactions',
                // Other qPilot-related props will be set dynamically
                enableCustomConfig: true,
                gtmStatsCustomConfigEntry: 'win_rollup_history',
            },
        },
        {
            id: 'oppCreationGoals',
            visible: true,
            order: 1.5,
            voiceover: "This metric tracks the number of new business opportunities that need to be created each month to hit revenue targets.",
            link: "#oppCreationGoals",
            title: "Opp Creation Goals",
            props: {
                chartTitle: 'opp creation goals',
                chartColorId: 'oppCreationGoals',
                usedInPredictions: false,
                availableGlobalOnly: false,
                drilldownAvailable: false,
                chartDescription: `Target number of new business opportunities to create per month, reverse-engineered from revenue goals using win rate, deal size, and sales cycle assumptions.`,
                inputType: 'integer',
                legendLabel: '# opps',
                color: '#4DA685',
                // Not a chart metric - used for goal setting only
                isGoalMetricOnly: true,
            },
        },
        {
            id: 'bookings',
            "visible": true,
            "order": 2,
            "voiceover": "This chart shows new business bookings.",
            link: "#bookings",
            title: "Bookings",
            props: {
                chartTitle: 'bookings',
                chartColorId: 'bookings',
                usedInPredictions: false,
                availableGlobalOnly: false,
                drilldownAvailable: true,
                chartDescription: `This chart shows the total new business {acceptedBookingMetricString} booked {segmentDescription}`,
                inputType: 'currency',
                handleChartClick: 'handleBookingsChartClick',
                legendLabel: '{acceptedBookingMetricString}',
                chartKeys: ['Closed Won'],
                enableCustomConfig: true,
                gtmStatsCustomConfigEntry: 'win_rollup_history',
                // qPilot entries are omitted as per your instruction
            },
        },

        {
            id: 'dealSize',
            "visible": true,
            "order": 2,
            "voiceover": "This chart shows average initial deal size.",
            link: "#dealSize",
            title: "Deal Size",
            props: {
                chartTitle: 'average initial deal size',
                chartColorId: 'dealSize',
                usedInPredictions: true,
                availableGlobalOnly: false,
                drilldownAvailable: true,
                chartDescription: `This chart shows average initial {acceptedBookingMetricString} of a new business deal {segmentDescription}`,
                inputType: 'currency',
                handleChartClick: 'handleDealSizeChartClick',
                legendLabel: '{acceptedBookingMetricString}',
                chartKeys: ['Average Deal Size'],
                enableCustomConfig: true,
                gtmStatsCustomConfigEntry: 'initial_deal_size',
                // qPilot entries are omitted as per your instruction
            },
        },

        {
            id: 'arr',
            "visible": true,
            "order": 2,
            "voiceover": "This chart shows ending ARR by period.",
            link: "#arr",
            title: "Annual Recurring Revenue",
            props: {
                chartTitle: 'ARR',
                chartColorId: 'arr',
                usedInPredictions: true,
                availableGlobalOnly: false,
                drilldownAvailable: true,
                chartDescription: `This chart shows ending ARR {segmentDescription}`,
                inputType: 'currency',
                // handleChartClick: 'handleDealSizeChartClick',
                // legendLabel: '{acceptedBookingMetricString}',
                chartKeys: ['Ending ARR'],
                enableCustomConfig: true,
                gtmStatsCustomConfigEntry: 'arr',
                // qPilot entries are omitted as per your instruction
            },
        },

        {
            id: 'nrr',
            "visible": true,
            "order": 2,
            "voiceover": "This chart shows ending ARR by period.",
            link: "#nrr",
            title: "Net Revenue Retention",
            props: {
                chartTitle: 'NRR',
                chartColorId: 'nrr',
                usedInPredictions: true,
                availableGlobalOnly: false,
                drilldownAvailable: true,
                chartDescription: `This chart shows ending ARR {segmentDescription}`,
                inputType: 'currency',
                // percent: 'handleDealSizeChartClick',
                // legendLabel: '{acceptedBookingMetricString}',
                chartKeys: ['NRR'],
                enableCustomConfig: true,
                gtmStatsCustomConfigEntry: 'arr',
                // qPilot entries are omitted as per your instruction
            },
        },
        {
            id: 'grr',
            "visible": true,
            "order": 2,
            "voiceover": "This chart shows ending ARR by period.",
            link: "#arr",
            title: "Annual Recurring Revenue",
            props: {
                chartTitle: 'GRR',
                chartColorId: 'grr',
                usedInPredictions: true,
                availableGlobalOnly: false,
                drilldownAvailable: true,
                chartDescription: `This chart shows ending ARR {segmentDescription}`,
                inputType: 'percent',
                // handleChartClick: 'handleDealSizeChartClick',
                // legendLabel: '{acceptedBookingMetricString}',
                chartKeys: ['GRR'],
                enableCustomConfig: true,
                gtmStatsCustomConfigEntry: 'arr',
                // qPilot entries are omitted as per your instruction
            },
        },
        {
            id: 'qualifiedOpps',
            visible: true,
            order: 3,
            voiceover: 'This chart shows new business opps qualified.',
            link: "#qualifiedOpps",
            title: "Opps Qualified",
            props: {
                chartTitle: 'opps qualified',
                chartColorId: 'qualifiedOpps',
                usedInPredictions: true,
                availableGlobalOnly: false,
                drilldownAvailable: true,
                chartDescription: `This chart shows the number of new business qualified opps {segmentDescription}.`,
                inputType: 'integer',
                bottomLegendLabel: 'Qualified Date',
                legendLabel: '# Qualified',
                handleChartClick: 'handleWinRateChartClick',
                // Additional props for QPilot
                qPilotEnabled: true,
                qPilotChartName: 'gtm-stats-qualified-opps',
                // Note: qPilotChartDataSet will be handled in utility functions
                enableCustomConfig: true,
                gtmStatsCustomConfigEntry: 'qualified_opps',
            },
        },

        {
            id: 'winRate',
            visible: true,
            order: 3,
            voiceover: 'This chart shows new business win rate.',
            link: "#winRate",
            title: "Win Rate",
            props: {
                chartTitle: 'win rate',
                chartColorId: 'winRate',
                usedInPredictions: true,
                availableGlobalOnly: false,
                drilldownAvailable: true,
                chartDescription: `This chart shows the new business win rate transaction {segmentDescription} Of the opportunities reaching a qualified stage in a given {dateType}, this is the % won on a count basis.`,
                inputType: 'percent',
                bottomLegendLabel: 'Qualified Date',
                legendLabel: 'Win Rate',
                handleChartClick: 'handleWinRateChartClick',
                // Additional props for QPilot
                qPilotEnabled: true,
                qPilotChartName: 'gtm-stats-win-rate',
                // Note: qPilotChartDataSet will be handled in utility functions
                enableCustomConfig: true,
                gtmStatsCustomConfigEntry: 'opp_win_rate',
            },
        },

        {
            id: 'salesCycle',
            visible: true,
            order: 4, // Adjust the order as needed
            voiceover: 'This chart shows the sales cycle in days for new business deals.',
            link: "#salesCycle",
            title: "Sales Cycle",
            props: {
                chartTitle: 'sales cycle',
                chartColorId: 'sales cycle',
                usedInPredictions: true,
                availableGlobalOnly: false,
                drilldownAvailable: true,
                chartDescription: `This chart shows the sales cycle in days for new business deals {segmentDescription} Sales cycle is the time between opp qualification and opp closed won.`,
                inputType: 'percent',
                bottomLegendLabel: 'days',
                bottomLegend: "days",
                legendLabel: '% Distribution',
                // handleChartClick: "handleSalesCycleChartClick",
                handleDistroChartClick: "handleSalesCycleChartClick",
                isDistroChart: true,
                isComposed: true,
                trendLineEntry: 'curve_values',
                showMarkers: false,
                showXMarkers: true,
                // Additional props for QPilot
                qPilotEnabled: true,
                qPilotChartName: 'gtm-stats-sales-cycle',
                qPilotExcludeDataWhenVerbose: true,
                // Note: qPilotChartDataSet will be handled in utility functions
                enableCustomConfig: true,
                gtmStatsCustomConfigEntry: 'opp_to_close_ts_stats',

            },
        },

        {
            id: 'quotaAttainment',
            visible: true,
            order: 6, // Adjust the order as needed
            voiceover: `This chart shows the distribution of quota attainment for quota-carrying sales reps globally across ${currUser.companyName}.`,
            link: "#quotaAttainment",
            title: "Quota Attainment",
            props: {
                chartTitle: 'quota attainment',
                chartColorId: 'quota attainment',
                groupByQuarter: true,
                usedInPredictions: false,
                availableGlobalOnly: true,
                drilldownAvailable: false,
                chartDescription: (
                    <>
                        This chart shows the distribution of quota attainment for quota-carrying sales reps globally
                        across {currUser.companyName}. To inspect theoretical outcomes based on current-quarter
                        forecasts,{' '}
                        <RouterLink
                            target={"_blank"}
                            style={{color: '#579FEE'}}
                            to={'/forecast/attainment'}
                        >
                            <b>see the attainment summary here</b>
                        </RouterLink>
                    </>),
                inputType: 'integer',
                dateType: 'quarter',
                showMarkers: false,
                legendLabel: '# Reps',
                bottomLegend: "# Reps",
                bottomLegendLabel: '# Reps',
                groupOrStacked: 'stacked',
                "colorsOverride": [
                    "#F47560FF",
                    "#F1E15BFF",
                    "#89D2C4FF"
                ],
                chartKeys: ['0 - 49.9%', '50 - 99.9%', '100% +'],
                // Additional props for QPilot
                qPilotEnabled: true,
                qPilotChartName: 'gtm-stats-quota-attainment',
                // qPilotChartDataSet will be handled in utility functions
            },
        },

        {
            id: 'mqlCreation',
            visible: true,
            order: 7, // Adjust the order as needed
            voiceover: `This chart shows ${mqlLabel || 'MQL'}s created by cohort along with any subsequent ${salLabel ? salLabel : 'raw opportunities'} created globally across ${currUser.companyName}.`,
            link: "#mqlCreation",
            title: "MQL Creation",
            props: {
                chartTitle: `leads handed off to sales (${mqlLabel || 'MQL'}s)`,
                chartColorId: 'leads handed off to sales',
                usedInPredictions: false,
                availableGlobalOnly: true,
                drilldownAvailable: true,
                showMarkers: true,
                handleChartClick: "handleMqlChartsClick",
                chartDescription: salLabel
                    ? `This chart shows ${mqlLabel || 'MQL'}s created by cohort along with any subsequent ${salLabel} conversions and subsequent opportunities created globally across ${currUser.companyName}.`
                    : `This chart shows ${mqlLabel || 'MQL'}s created by cohort and subsequent raw opportunities created globally across ${currUser.companyName}.`,
                inputType: 'integer',
                legendLabel: '#',
                groupOrStacked: 'grouped',
                bottomLegendLabel: `${mqlLabel || "MQL"} Date`,
                // Additional props for QPilot
                qPilotEnabled: true,
                qPilotChartName: 'gtm-stats-mql-opp-conversions',
                // qPilotChartDataSet will be handled in utility functions
            },
        },


        {
            id: 'mqlToSalConversionRate',
            visible: salLabel && salLabel !== null,
            order: 8, // Adjust the order as needed
            voiceover: `This chart shows the percentage of ${mqlLabel || 'MQL'}s that converted into ${salLabel || 'SAL'}s globally across ${currUser.companyName}.`,
            link: "#mqlToSalConversionRate",
            title: `${mqlLabel || "MQL"} > ${salLabel || "opps created"}`,
            props: {
                chartTitle: `${mqlLabel || 'MQL'} > ${salLabel || 'SAL'} conversion rate`,
                chartColorId: 'MQL > SAL conversion rate',
                usedInPredictions: false,
                availableGlobalOnly: true,
                drilldownAvailable: false,
                chartDescription: `This chart shows the % of ${mqlLabel || 'MQL'}s that converted into ${salLabel || 'SAL'}s globally across ${currUser.companyName}.`,
                inputType: 'percent',
                yAxisAutoScale: true,
                dateType: 'month',
                showMarkers: false,
                legendLabel: '% Converted',
                groupOrStacked: 'grouped',
                bottomLegendLabel: `${mqlLabel || "MQL"} Date`,
                showCumulativeAverageLine: true,
                lineEntry: 'MQL > SAL Average',
                chartKeys: [`MQL > ${salLabel || 'SAL'} %`],
            },
        },

        {
            id: 'mqlToOppCreatedConversionRate',
            visible: mqlLabel && mqlLabel !== null,
            order: 9, // Adjust the order as needed
            voiceover: `This chart shows the percentage of ${mqlLabel || 'MQL'}s that converted into raw opportunity records globally across ${currUser.companyName}.`,
            link: "#mqlToOppCreatedConversionRate",
            title: `${mqlLabel || "MQL"} > ${salLabel || "opp timing"}`,
            props: {
                chartTitle: `${mqlLabel || 'MQL'} > opp created conversion rate`,
                chartColorId: 'MQL > opp created conversion rate',
                usedInPredictions: false,
                availableGlobalOnly: true,
                drilldownAvailable: false,
                chartDescription: `This chart shows the % of ${mqlLabel || 'MQL'}s that converted into raw opportunity records globally across ${currUser.companyName}.`,
                inputType: 'percent',
                yAxisAutoScale: true,
                dateType: 'month',
                showMarkers: false,
                legendLabel: '% Converted',
                groupOrStacked: 'grouped',
                bottomLegendLabel: `${mqlLabel || "MQL"} Date`,
                showCumulativeAverageLine: true,
                lineEntry: 'MQL > Opp Created Average',
                chartKeys: ['MQL > Opp Created %'],
            },
        },

        {
            id: 'mqlConversionTiming',
            visible: mqlLabel && mqlLabel !== null,
            order: 10, // Adjust the order as needed
            voiceover: `This chart shows the distribution in days that it takes for sales to accept a ${mqlLabel || 'MQL'}.`,
            link: "#mqlConversionTiming", title: "MQL Timing",

            props: {
                chartTitle: `${mqlLabel ? mqlLabel : 'MQL'} > opp created conversion timing`,
                chartColorId: 'MQL > opp created conversion timing',
                usedInPredictions: false,
                availableGlobalOnly: true,
                drilldownAvailable: false,
                chartDescription: `This chart shows the distribution in days that it takes to convert an ${mqlLabel}.`,
                // QPilot Props
                bottomLegend: "days",
                bottomLegendLabel: 'days',
                chartKeys: ['hist_values'],
                dateType: 'days',
                groupOrStacked: 'grouped',
                inputType: 'percent',
                isComposed: true,
                isDistroChart: true,
                legendLabel: '% Distribution',
                lineEntry: 'MQL > Opp Created Average',
                showCumulativeAverageLine: true,
                showMarkers: false,
                showXMarkers: true,
                trendLineEntry: 'curve_values',
            },
        },


        {
            id: 'mqlToOppQualifiedConversionRate',
            visible: true,
            order: 11, // Adjust the order as needed
            voiceover: `This chart shows the percentage of ${mqlLabel || 'MQL'}s that converted into qualified opportunity records globally across ${currUser.companyName}.`,
            link: "#mqlToOppQualifiedConversionRate",
            title: `${mqlLabel || "MQL"} > ${salLabel || "qualified"}`,
            props: {
                chartTitle: `${mqlLabel || 'MQL'} > opp qualified conversion rate`,
                chartColorId: 'MQL > opp qualified conversion rate',
                usedInPredictions: false,
                availableGlobalOnly: true,
                drilldownAvailable: false,
                yAxisAutoScale: true,
                chartDescription: `This chart shows the % of ${mqlLabel || 'MQL'}s that converted into qualified opportunity records globally across ${currUser.companyName}.`,
                inputType: 'percent',
                dateType: 'month',
                showMarkers: false,
                legendLabel: '% Converted',
                groupOrStacked: 'grouped',
                bottomLegendLabel: `${mqlLabel || "MQL"} Date`,
                showCumulativeAverageLine: true,
                lineEntry: 'MQL > Opp Qualified Average',
                chartKeys: [`MQL > Opp Qualified %`],
            },
        },

        {
            id: 'prospectCreation',
            visible: true,
            order: 12, // Adjust the order as needed
            voiceover: 'This chart shows the rate at which marketing and sales are engaging net new and unique companies.',
            link: "#prospectCreation",
            title: "Prospect Creation",
            props: {
                chartTitle: 'prospect creation',
                chartColorId: 'prospect creation',
                usedInPredictions: true,
                availableGlobalOnly: true,
                chartDescription: `This chart shows the rate at which marketing and sales are engaging net new and unique companies, regardless of whether those companies are made up of leads, contacts, or prospects in your business systems.`,
                inputType: 'integer',
                bottomLegendLabel: 'Normalized Date Created',
                legendLabel: 'Normalized Date Created',
                handleChartClick: undefined, // No click handler specified
                drilldownAvailable: false,
                // Additional props for QPilot
                qPilotEnabled: true,
                qPilotChartName: 'gtm-stats-prospect-creation',
                // qPilotChartDataSet will be handled in utility functions
            },
        },


        {
            id: 'prospectConversionTiming',
            visible: true,
            order: 13, // Adjust the order as needed
            voiceover: `This chart shows the average distribution in days between prospect creation and a conversion into qualified opportunities.`,
            link: "#prospectConversionDays", title: "Prospect Timing",
            props: {
                availableGlobalOnly: false,
                bottomLegend: "days",
                bottomLegendLabel: 'days',
                chartColorId: 'prospect conversion timing',
                chartDescription: `This chart shows the sales cycle in days for new business deals {segmentDescription} Sales cycle is the time between opp qualification and opp closed won.`,
                chartTitle: 'prospect conversion timing',
                drilldownAvailable: false,
                enableCustomConfig: true,
                gtmStatsCustomConfigEntry: 'opp_to_close_ts_stats',
                handleDistroChartClick: "handleProspectsCycleChartClick",
                inputType: 'percent',
                isComposed: true,
                isDistroChart: true,
                legendLabel: '% Distribution',
                showMarkers: false,
                showXMarkers: true,
                trendLineEntry: 'curve_values',
                usedInPredictions: true,
            },
        },

        {
            id: 'activityLevels',
            visible: true,
            order: 14, // Adjust the order as needed
            voiceover: `This chart shows the number of activities broken down by label, indicating the engagement levels of marketing and sales teams across different segments.`,
            link: "#activityLevels", title: "Activity Levels",
            props: {
                // Additional Props for Distribution Charts
                // QPilot Props
                availableGlobalOnly: true,
                bottomLegendLabel: 'Activity Date',
                chartColorId: 'activity levels',
                chartDescription: `This chart shows the number of activities broken down by label, indicating the engagement levels of marketing and sales teams.`,
                chartTitle: 'activity levels',
                drilldownAvailable: false,
                groupOrStacked: 'stacked',
                inputType: 'integer',
                isComposed: false,
                isDistroChart: false,
                isDummyData: false,
                legendLabel: '# activities',
                qPilotChartName: 'gtm-stats-activity-levels',
                qPilotChartDataSet: [], // Will be handled in helper functions
                qPilotEnabled: true,
                qPilotImportantCols: [], // Will be handled in helper functions
                qPilotSupplementalPrompt: '', // Will be handled in helper functions
                usedInPredictions: false,

            },
        },

        {
            id: 'accountsTouched',
            visible: true,
            order: 15, // Adjust the order as needed
            voiceover: `This chart shows the number of accounts touched by different stages: Pre-Opp, In sales cycle, and Post-Win.`,
            link: "#accountsTouched", title: "Accounts Touched",
            props: {
                chartTitle: 'accounts touched',
                chartColorId: 'accounts touched',
                usedInPredictions: false,
                availableGlobalOnly: false,
                drilldownAvailable: true,
                chartDescription: `This chart shows the number of accounts touched by month, broken down into Pre-Opp, In sales cycle, and Post-Win stages.`,
                inputType: 'integer',
                bottomLegendLabel: 'Activity Date',
                legendLabel: '# accounts',
                groupOrStacked: 'stacked',
                chartKeys: ['Pre-Opp', 'In sales cycle', 'Post-Win'],
                isDistroChart: false,
                isComposed: false,
                showMarkers: false,
                // QPilot Props
                qPilotEnabled: true,
                qPilotChartName: 'gtm-stats-accounts-touched',
                qPilotChartDataSet: [], // To be handled in helper functions
                qPilotSupplementalPrompt: '', // To be handled in helper functions
                qPilotImportantCols: [], // To be handled in helper functions
                // Additional Props for Distribution Charts
                isDummyData: false,
            },
        },
        {
            id: 'pipelineCreated',
            visible: true,
            order: 16, // Adjust the order as needed
            voiceover: `This chart breaks down new pipeline created by month.`,
            link: "#pipelineCreated", title: "Pipeline Created",
            props: {
                chartTitle: 'pipeline created',
                chartColorId: 'pipeline created',
                usedInPredictions: false,
                availableGlobalOnly: false,
                drilldownAvailable: true,
                handleChartClick: "handlePipelineCreatedClick",
                chartDescription: `This chart breaks down new pipeline created by month.`,
                inputType: 'currency',
                legendLabel: `Pipeline ${acceptedBookingMetricString} created`,
                groupOrStacked: 'grouped',
                maxValue: undefined, // To be set via helper functions
                chartKeys: ['Pipeline Created'],
                isDistroChart: false,
                isComposed: false,
                trendLineEntry: 'trend_line', // Adjust based on your data structure
                // QPilot Props
                qPilotEnabled: true,
                qPilotChartName: 'gtm-stats-pipeline-creation',
                qPilotChartDataSet: [], // To be handled in helper functions
                qPilotSupplementalPrompt: '', // To be handled in helper functions
                qPilotImportantCols: ['Qualified Month', 'Pipeline Created'],
                // Additional Props for Distribution Charts
                isDummyData: false,
            },
        },

        {
            id: 'pipelineBreakdown',
            visible: true,
            order: 17, // Adjust the order as needed
            voiceover: `This chart breaks down the pipeline at the end of each month based on resolved sources, including Sales, Marketing, Partners, and Unknown.`,
            link: "#pipelineBreakdown", title: "Pipeline Breakdown",
            props: {
                chartTitle: 'pipeline breakdown',
                chartColorId: 'pipeline breakdown',
                customColors: true,
                usedInPredictions: false,
                availableGlobalOnly: false,
                drilldownAvailable: true,
                chartDescription: `This chart breaks down the pipeline at the end of each month based on resolved sources. A detailed view is available under Pipeline Forensics > Change Inspector > Pipeline history.`,
                inputType: 'currency',
                handleChartClick: "handlePipelineBreakdownClick",
                legendLabel: `Pipeline ${acceptedBookingMetricString}`,
                groupOrStacked: 'stacked',
                showMarkers: false,
                maxValue: undefined, // To be set via helper functions
                chartKeys: [], // To be set via helper functions
                isDistroChart: false,
                isComposed: false,
                // trendLineEntry: 'trend_line', // Adjust based on your data structure
                // QPilot Props
                qPilotEnabled: true,
                qPilotChartName: 'gtm-stats-pipeline-breakdown',
                qPilotChartDataSet: [], // To be handled in helper functions
                qPilotSupplementalPrompt: '', // To be handled in helper functions
                qPilotImportantCols: ['Qualified Month', 'Sales', 'Marketing', 'Partners', 'BDR', 'Unknown'],
                // Additional Props for Distribution Charts
                isDummyData: false,
            },
        },

        // Include other charts as needed
    ]
}